import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { AdminPackageDetail } from '../types/packages';
import PackageStatusChip from '../components/packages/PackageStatusChip';
import PackageEditDetails from '../components/packages/PackageEditDetails';
import PackageEditPricing from '../components/packages/PackageEditPricing';
import PackageEditItems from '../components/packages/PackageEditItems';
import { PACKAGE_STATUS_LABELS, packageDecisionError } from '../components/packages/packageReviewModel';
import {
  changedPackageFields, formFromPackage, formToPackageRequest, mapPackageEditError, START_LOCKED_STATUSES, validatePackageEdit,
  type PackageEditErrors, type PackageEditField, type PackageEditForm,
} from '../components/packages/packageEditModel';
import { minEndDate, todayInDubai } from '../components/offers/offerEditModel';

/** Spec 081b — `/service-approvals/packages/:id/edit`: an admin edits a package in place (status kept, garage notified). */
export default function PackageEditPage() {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const today = useMemo(() => todayInDubai(), []);
  const reviewPath = `/service-approvals/packages/${id}`;

  const [detail, setDetail] = useState<AdminPackageDetail | null>(null);
  const [original, setOriginal] = useState<PackageEditForm | null>(null);
  const [form, setForm] = useState<PackageEditForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [errors, setErrors] = useState<PackageEditErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [needsReload, setNeedsReload] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!auth.token || !id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const d = await api.getPackageForReview(id, auth.token);
      const f = formFromPackage(d);
      setDetail(d);
      setOriginal(f);
      setForm(f);
      setErrors({});
      setFormError(null);
      setNeedsReload(false);
      setBlocked(d.status === 'ENDED');
    } catch (err) {
      setLoadError(packageDecisionError(err, 'load').message);
    } finally {
      setLoading(false);
    }
  }, [auth.token, id]);

  useEffect(() => { load(); }, [load]);

  const changes = useMemo(
    () => (detail && form && original ? changedPackageFields(original, form, detail.pricingMode) : []),
    [detail, form, original],
  );

  if (loading && !detail) {
    return <div className="admin-loading"><div className="admin-loading-spinner"></div></div>;
  }
  if (!detail || !form || !original) {
    return (
      <div>
        <Link to={reviewPath} className="admin-link">← Back to package</Link>
        <div className="admin-info-banner danger" role="alert" style={{ marginTop: '1rem' }}>{loadError ?? 'Could not load this package.'}</div>
      </div>
    );
  }

  const disabled = saving || blocked;
  const update = (key: keyof PackageEditForm, value: string | null, clear: PackageEditField[] = [key as PackageEditField]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setFormError(null);
    setErrors((e) => { const n = { ...e }; clear.forEach((k) => delete n[k]); return n; });
  };

  const save = async () => {
    const errs = validatePackageEdit(form, {
      mode: detail.pricingMode, originalTotal: detail.originalTotal, originalStartsAt: original.startsAt, today,
    });
    if (Object.keys(errs).length) {
      setErrors(errs);
      setFormError('Fix the highlighted fields.');
      return;
    }
    setSaving(true);
    try {
      await api.updatePackageAsAdmin(detail.id, formToPackageRequest(form, detail), auth.token!);
      navigate(reviewPath, { state: { toast: 'Package updated. The garage has been notified.' } });
    } catch (err) {
      const mapped = mapPackageEditError(err);
      setErrors(mapped.fieldErrors);
      setFormError(mapped.formError);
      setNeedsReload(mapped.reload);
      setBlocked(mapped.blocked);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="offer-review offer-edit package-edit">
      <div className="offer-review__header">
        <Link to={reviewPath} className="admin-link">← Back to package</Link>
        <h2 className="offer-review__title">Edit package</h2>
        <div className="offer-edit__subhead">
          <span className="admin-text-muted">{detail.garage.name}</span>
          <PackageStatusChip status={detail.status} />
        </div>
      </div>

      {detail.status === 'ENDED' ? (
        <div className="admin-info-banner warning" role="status" data-testid="package-edit-blocked">This package has ended and can no longer be edited.</div>
      ) : (
        <div className="admin-info-banner info" style={{ marginBottom: '1rem' }}>
          Saving applies at once and keeps the package {PACKAGE_STATUS_LABELS[detail.status].toLowerCase()}. The garage is notified.
        </div>
      )}
      {formError && (
        <div className="admin-info-banner danger" role="alert" data-testid="package-edit-error" style={{ marginBottom: '1rem' }}>
          {formError}
          {needsReload && (
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" style={{ marginLeft: '0.75rem' }} onClick={load}>Reload</button>
          )}
        </div>
      )}

      <div className="package-edit__form">
        <PackageEditDetails form={form} errors={errors} disabled={disabled}
          startLocked={START_LOCKED_STATUSES.includes(detail.status)} minEnd={minEndDate(form.startsAt, today)}
          onChange={(key, value, clear) => update(key, value, clear)} />
        <PackageEditPricing pkg={detail} form={form} errors={errors} disabled={disabled} onChange={(key, value) => update(key, value)} />
        <PackageEditItems pkg={detail} form={form} errors={errors} disabled={disabled}
          onRemoveCover={() => { update('coverStorageKey', null); update('coverUrl', null, []); }}
          onRestoreCover={() => { update('coverStorageKey', original.coverStorageKey); update('coverUrl', original.coverUrl, []); }} />
      </div>

      <div className="offer-edit__bar">
        <span className="admin-text-muted" data-testid="package-edit-changes">{changes.length ? `Changed: ${changes.join(', ')}` : 'No changes yet'}</span>
        <div className="offer-edit__bar-actions">
          <Link to={reviewPath} className="admin-btn admin-btn-outline">Cancel</Link>
          <button type="button" className="admin-btn admin-btn-primary" disabled={disabled || changes.length === 0} onClick={save}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
